import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatCurrency, downloadAuthedFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Wallet, CheckCircle2, Circle, PencilLine, Plus, X, Users, Clock, FileText } from "lucide-react";
import ResponsiveTable from "../components/ResponsiveTable";

/**
 * Payroll — simple monthly salary view for Indonesian staff payroll.
 *
 * Scope (per user spec):
 *   Gaji Pokok + Tunjangan + Komisi (auto) + Bonus + THR → Total → Paid/Unpaid
 *
 * Access: admin + accountant only. Sales role sees nothing here.
 *
 * The month is a URL-free local state (defaults to current month). Admin can
 * back-fill payroll for prior months by switching the picker.
 */
export default function Payroll() {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.role === "accountant";

  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileEdit, setProfileEdit] = useState(null);  // user row being edited
  const [bonusEdit, setBonusEdit] = useState(null);  // user row for bonus/thr modal

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payroll?month=${month}`);
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load payroll");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (allowed) load(); /* eslint-disable-next-line */ }, [month, allowed]);

  if (user === null) return null;
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const togglePaid = async (row) => {
    try {
      await api.post(`/payroll/users/${row.user_id}/pay?month=${month}`);
      toast.success(`${row.name} · ${row.paid ? "Unpaid" : "Paid"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed");
    }
  };

  const downloadSlip = async (row) => {
    try {
      const safeName = (row.name || "karyawan").replace(/\s+/g, "_").slice(0, 40);
      await downloadAuthedFile(
        `/payroll/users/${row.user_id}/payslip.pdf?month=${month}`,
        `slip_gaji_${safeName}_${month}.pdf`,
        { errorMessage: "Tidak dapat mengunduh slip gaji" },
      );
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  return (
    <Layout title="Payroll" subtitle="Gaji bulanan & komisi staff">
      <Toaster theme="dark" position="top-right" />

      {/* Month picker + headcount */}
      <div className="flex flex-wrap items-center gap-3 mb-7">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="overline">Periode</span>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="luxe-input px-3 py-2 text-sm mono"
            data-testid="payroll-month"
          />
        </div>
        {data && (
          <div className="flex items-center gap-2 text-xs text-[#8A8A8A] ml-auto">
            <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span data-testid="payroll-headcount">{data.totals.headcount} staff</span>
            <span className="text-[#666] mx-1">·</span>
            <span className="text-[#7BA585]" data-testid="payroll-paid-count">
              {data.totals.paid_count} sudah dibayar
            </span>
            <span className="text-[#666] mx-1">·</span>
            <span className="text-[#C99356]" data-testid="payroll-unpaid-count">
              {data.totals.unpaid_count} belum dibayar
            </span>
          </div>
        )}
      </div>

      {/* Summary tiles */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-7">
          <SummaryTile label="Total Take-home" value={formatCurrency(data.totals.total)} accent />
          <SummaryTile label="Gaji Pokok" value={formatCurrency(data.totals.base_salary)} />
          <SummaryTile label="Tunjangan" value={formatCurrency(data.totals.allowances)} />
          <SummaryTile label="Bonus + THR" value={formatCurrency(data.totals.bonus + data.totals.thr)} />
          <SummaryTile label="Komisi" value={formatCurrency(data.totals.commission)} />
        </div>
      )}

      {/* Paid vs Unpaid amounts */}
      {data && (data.totals.paid_total > 0 || data.totals.unpaid_total > 0) && (
        <div className="grid grid-cols-2 gap-4 mb-7">
          <div className="border border-[#7BA585]/30 bg-[#7BA585]/[0.04] p-4">
            <div className="overline text-[#7BA585]">Sudah Dibayar</div>
            <div className="serif text-xl mt-1 text-[#7BA585]">{formatCurrency(data.totals.paid_total)}</div>
          </div>
          <div className="border border-[#C99356]/30 bg-[#C99356]/[0.04] p-4">
            <div className="overline text-[#C99356]">Belum Dibayar</div>
            <div className="serif text-xl mt-1 text-[#C99356]">{formatCurrency(data.totals.unpaid_total)}</div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading && !data ? (
        <div className="text-[#8A8A8A] text-sm py-10 text-center">Loading…</div>
      ) : data && data.rows.length === 0 ? (
        <div className="border border-white/5 px-5 py-10 text-center text-[#8A8A8A] text-sm">
          No staff configured.
        </div>
      ) : data ? (
        <PayrollTable
          rows={data.rows}
          onEditProfile={setProfileEdit}
          onEditBonus={setBonusEdit}
          onTogglePaid={togglePaid}
          onDownloadSlip={downloadSlip}
        />
      ) : null}

      {profileEdit && (
        <SalaryProfileModal
          row={profileEdit}
          onClose={() => setProfileEdit(null)}
          onSaved={() => { setProfileEdit(null); load(); }}
        />
      )}
      {bonusEdit && (
        <BonusThrModal
          row={bonusEdit}
          month={month}
          onClose={() => setBonusEdit(null)}
          onSaved={() => { setBonusEdit(null); load(); }}
        />
      )}
    </Layout>
  );
}

function SummaryTile({ label, value, accent }) {
  return (
    <div className={`border p-4 ${accent ? "border-[#D4AF37]/30 bg-[#D4AF37]/[0.04]" : "border-white/5 bg-white/[0.02]"}`}>
      <div className="overline text-[0.55rem] text-[#7a7a7a]">{label}</div>
      <div className={`serif text-lg mt-1 ${accent ? "text-[#D4AF37]" : "text-[#EAEAEA]"}`}>{value}</div>
    </div>
  );
}

function PayrollTable({ rows, onEditProfile, onEditBonus, onTogglePaid, onDownloadSlip }) {
  const columns = [
    {
      key: "name",
      label: "Staff",
      width: "22%",
      cell: (r) => (
        <div className="min-w-0">
          <div className="text-[#EAEAEA] truncate" data-testid={`payroll-name-${r.user_id}`}>{r.name}</div>
          <div className="text-[0.65rem] mono text-[#666] mt-0.5 truncate">{r.email}</div>
          <div className="overline text-[0.55rem] mt-1">{r.role}</div>
        </div>
      ),
    },
    { key: "base_salary", label: "Gaji Pokok", align: "right", width: "13%",
      cell: (r) => <span className="mono text-xs">{formatCurrency(r.base_salary)}</span> },
    { key: "allowances", label: "Tunjangan", align: "right", width: "13%",
      cell: (r) => <span className="mono text-xs text-[#8A8A8A]">{formatCurrency(r.allowances)}</span>, hideOnMobile: true },
    { key: "commission", label: "Komisi", align: "right", width: "12%",
      cell: (r) => (
        <span className={`mono text-xs ${r.commission > 0 ? "text-[#7BA585]" : "text-[#666]"}`}>
          {formatCurrency(r.commission)}
        </span>
      ), hideOnMobile: true,
    },
    { key: "bonus", label: "Bonus + THR", align: "right", width: "14%",
      cell: (r) => (
        <span className={`mono text-xs ${(r.bonus + r.thr) > 0 ? "text-[#D4AF37]" : "text-[#666]"}`}>
          {formatCurrency(r.bonus + r.thr)}
        </span>
      ), hideOnMobile: true,
    },
    { key: "total", label: "Total", align: "right", width: "14%",
      cell: (r) => <span className="mono text-sm text-[#D4AF37] font-semibold">{formatCurrency(r.total)}</span>,
    },
    { key: "status", label: "Status", align: "center", width: "12%",
      cell: (r) => (
        <button
          onClick={() => onTogglePaid(r)}
          data-testid={`toggle-paid-${r.user_id}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[0.65rem] uppercase tracking-wider border transition-colors ${
            r.paid
              ? "border-[#7BA585]/40 text-[#7BA585] bg-[#7BA585]/[0.05] hover:bg-[#7BA585]/10"
              : "border-[#C99356]/40 text-[#C99356] bg-[#C99356]/[0.05] hover:bg-[#C99356]/10"
          }`}
        >
          {r.paid ? <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> : <Circle className="w-3 h-3" strokeWidth={2} />}
          {r.paid ? "Dibayar" : "Belum"}
        </button>
      ),
    },
  ];

  const rowActions = (r) => (
    <>
      <button
        onClick={() => onDownloadSlip(r)}
        title="Unduh Slip Gaji (PDF)"
        data-testid={`download-slip-${r.user_id}`}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37]"
      >
        <FileText className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <button
        onClick={() => onEditProfile(r)}
        title="Edit gaji pokok & tunjangan"
        data-testid={`edit-salary-${r.user_id}`}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37]"
      >
        <PencilLine className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <button
        onClick={() => onEditBonus(r)}
        title="Tambah bonus / THR bulan ini"
        data-testid={`edit-bonus-${r.user_id}`}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37]"
      >
        <Plus className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </>
  );

  return (
    <ResponsiveTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.user_id}
      rowActions={rowActions}
      emptyMessage="No staff configured."
      mobileCard={(r) => ({
        title: <span className="text-[#EAEAEA]">{r.name}</span>,
        subtitle: <span className="overline text-[0.55rem]">{r.role} · {r.email}</span>,
        fields: [
          { label: "Gaji Pokok", value: <span className="mono">{formatCurrency(r.base_salary)}</span> },
          { label: "Tunjangan", value: <span className="mono text-[#8A8A8A]">{formatCurrency(r.allowances)}</span> },
          { label: "Komisi", value: <span className="mono text-[#7BA585]">{formatCurrency(r.commission)}</span> },
          { label: "Bonus + THR", value: <span className="mono text-[#D4AF37]">{formatCurrency(r.bonus + r.thr)}</span> },
          { label: "Total", value: <span className="mono text-[#D4AF37] font-semibold">{formatCurrency(r.total)}</span> },
          { label: "Status", value: r.paid
              ? <span className="text-[#7BA585]">✓ Dibayar</span>
              : <span className="text-[#C99356]">Belum dibayar</span> },
        ],
        actions: rowActions(r),
      })}
    />
  );
}

function SalaryProfileModal({ row, onClose, onSaved }) {
  const [base, setBase] = useState(row.base_salary || 0);
  const [allow, setAllow] = useState(row.allowances || 0);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/payroll/users/${row.user_id}/profile`, {
        base_salary: Number(base) || 0,
        allowances: Number(allow) || 0,
      });
      toast.success(`Gaji ${row.name} disimpan`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Edit Gaji" subtitle={`${row.name} · ${row.role}`} onClose={onClose} testid="salary-profile-modal">
      <form onSubmit={submit} className="px-7 py-6 space-y-4">
        <div className="border border-[#D4AF37]/15 bg-[#D4AF37]/[0.03] px-3 py-2 text-[0.7rem] text-[#8A8A8A] leading-relaxed">
          Berlaku untuk setiap bulan setelahnya, sampai diubah lagi. Bulan-bulan
          sebelumnya tidak terpengaruh.
        </div>
        <Field label="Gaji Pokok (IDR / bulan)">
          <input
            type="number"
            min="0"
            step="1000"
            value={base}
            onChange={(e) => setBase(e.target.value)}
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="salary-base-input"
            required
          />
        </Field>
        <Field label="Tunjangan (transport, makan, dll — IDR / bulan)">
          <input
            type="number"
            min="0"
            step="1000"
            value={allow}
            onChange={(e) => setAllow(e.target.value)}
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="salary-allowances-input"
          />
        </Field>
        <div className="border-t border-white/5 pt-3 flex justify-between items-baseline">
          <span className="overline text-[#D4AF37]">Total per bulan</span>
          <span className="serif text-xl text-[#D4AF37]" data-testid="salary-monthly-preview">
            {formatCurrency((Number(base) || 0) + (Number(allow) || 0))}
          </span>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-gold px-5 py-2.5"
            data-testid="salary-profile-save"
          >
            {submitting ? "Saving…" : "Simpan"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function BonusThrModal({ row, month, onClose, onSaved }) {
  const [bonus, setBonus] = useState(row.bonus || 0);
  const [thr, setThr] = useState(row.thr || 0);
  const [notes, setNotes] = useState(row.notes || "");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/payroll/users/${row.user_id}/record?month=${month}`, {
        bonus: Number(bonus) || 0,
        thr: Number(thr) || 0,
        notes,
      });
      toast.success(`Bonus / THR ${row.name} disimpan`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Bonus & THR"
      subtitle={`${row.name} · ${monthLabel(month)}`}
      onClose={onClose}
      testid="bonus-thr-modal"
    >
      <form onSubmit={submit} className="px-7 py-6 space-y-4">
        <Field label="Bonus bulan ini (IDR)">
          <input
            type="number"
            min="0"
            step="1000"
            value={bonus}
            onChange={(e) => setBonus(e.target.value)}
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="bonus-input"
          />
        </Field>
        <Field label="THR — Tunjangan Hari Raya (IDR)">
          <input
            type="number"
            min="0"
            step="1000"
            value={thr}
            onChange={(e) => setThr(e.target.value)}
            className="luxe-input w-full px-4 py-2.5 text-sm mono"
            data-testid="thr-input"
          />
        </Field>
        <Field label="Catatan (opsional)">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. performance bonus Q2"
            className="luxe-input w-full px-4 py-2.5 text-sm"
            data-testid="bonus-notes"
          />
        </Field>
        <div className="border-t border-white/5 pt-3 flex justify-between items-baseline">
          <span className="overline text-[#D4AF37]">Bonus + THR bulan ini</span>
          <span className="serif text-xl text-[#D4AF37]" data-testid="bonus-thr-preview">
            {formatCurrency((Number(bonus) || 0) + (Number(thr) || 0))}
          </span>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-gold px-5 py-2.5"
            data-testid="bonus-thr-save"
          >
            {submitting ? "Saving…" : "Simpan"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, subtitle, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className="bg-[#0F0F0F] border border-[#D4AF37]/20 w-full max-w-md fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-start">
          <div>
            <div className="overline text-[#D4AF37]"><Wallet className="w-3 h-3 inline mr-1" /> Payroll</div>
            <h3 className="serif text-xl mt-1">{title}</h3>
            {subtitle && <div className="text-xs text-[#8A8A8A] mt-1">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-[#8A8A8A] hover:text-[#EAEAEA]">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
    </div>
  );
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
  return `${months[parseInt(mo, 10) - 1]} ${y}`;
}
